import React from 'react';
import { ICellEditorParams, ICellRendererParams } from 'ag-grid-community';

import StateOverrideRendererFramework from '../displayComponents/stateOverrideRenderer/stateOverrideRendererFramework.component';
import ConditionalSelectionListEditorSelectorFactory from '../displayComponents/editorSelector/conditionalSelectionListEditorSelector.factory';
import { DataTypes } from '../enums';
import {
  TextCellRendererFactory,
  EditableCheckboxCellRenderer,
} from '../../shared/columnDefinitions/cellRenderers';

import { NodeLevel } from './constants';
import { cellRenderer } from './cellRenderers/cellRenderer.component';

import { defaultColumnDefinitionWithFilter, getDefaultGroupColumnDefinitionWithFilter } from '.';

interface CheckboxGroup {
  id: string;
  name: string;
  description: string;
}

interface Node {
  group: boolean;
  key: string;
  level: NodeLevel;
}

interface Row {
  dataType: DataTypes;
  checkboxGroupId: string;
  aliasName: string;
  description: string;
  value: string;
  itemName: string;
  itemDescription: string;
  itemDisplayName: string;
}

interface Cell {
  node: Node;
  data: Row;
  value: string;
  oldValue: string;
  newValue: string;
}

interface CellEditorParams extends ICellEditorParams {
  colDef: {
    field: string;
  };
}

const TextRenderer = TextCellRendererFactory({
  getText: ({ value }: { value: string }) => value || '',
});

const groupLabelForRowSort = (
  checkboxGroups: CheckboxGroup[],
  value: string | null | undefined,
) => {
  if (value == null || value === '') {
    return '';
  }
  const id = String(value);
  return checkboxGroups.find(g => g.id === id)?.name || id;
};

const RendererFramework = (props: CellEditorParams) => {
  if (props.data.dataType === DataTypes.CHECKBOX) {
    return <EditableCheckboxCellRenderer {...props} />;
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: missing properties from type 'Pick<ICellRendererParamsWithValidators
  return <TextRenderer {...props} />;
};

export const getCheckBoxGroupNameColumnDefinition = ({
  checkboxGroups,
}: {
  checkboxGroups: CheckboxGroup[];
}) => {
  const groupNameValueGetter = ({ value }: { value: string }) =>
    checkboxGroups.find(checkboxGroup => checkboxGroup.id === value)?.name || '';

  return {
    ...defaultColumnDefinitionWithFilter,
    headerName: 'Group/Account',
    field: 'checkboxGroupId',
    rowGroup: true,
    hide: true,
    cellRenderer: groupNameValueGetter,
    comparator: (valueA: string | null | undefined, valueB: string | null | undefined) =>
      groupLabelForRowSort(checkboxGroups, valueA).localeCompare(
        groupLabelForRowSort(checkboxGroups, valueB),
        undefined,
        { sensitivity: 'base' },
      ),
  };
};

export const getCheckBoxGroupDescriptionColumnDefinition = ({
  checkboxGroups,
}: {
  checkboxGroups: CheckboxGroup[];
}) => {
  const groupDescriptionValueGetter = ({
    node: { group: isCheckboxGroup, key },
    data,
    value,
  }: ICellRendererParams) => {
    if (isCheckboxGroup && checkboxGroups.length > 0) {
      return checkboxGroups.find(row => row.id === key)?.description || '';
    }

    if (data?.dataType === DataTypes.CHECKBOX && data?.checkboxGroupId && data?.itemDescription) {
      return data.itemDescription;
    }

    return value;
  };

  return {
    ...defaultColumnDefinitionWithFilter,
    headerName: 'Description',
    field: 'description',
    minWidth: 280,
    flex: 2,
    cellRenderer: (params: ICellRendererParams) =>
      cellRenderer({ params, renderer: groupDescriptionValueGetter }),
  };
};

export const getCheckBoxGroupValueColumnDefinition = ({
  rowData,
  getRowData,
  isInEditMode,
  updateGroup,
  updateRow,
  filingTypeId,
}: {
  /** Live row list; prefer with useMemo that omits row data from deps (use getRowData instead). */
  rowData?: Row[];
  /** When set, called to read the current row list (e.g. ref). Avoids new columnDefs on every API/row refresh. */
  getRowData?: () => Row[];
  isInEditMode: boolean;
  updateGroup: (modifiedGroupRow: Array<unknown>) => void;
  updateRow: (modifiedGroupRow: { value: string }) => void;
  filingTypeId?: string;
}) => {
  const resolveRows = (): Row[] => (getRowData ? getRowData() : rowData ?? []);

  const conditionalEditorSelector = ConditionalSelectionListEditorSelectorFactory();

  const onCellValueChanged = ({ node: { group: isCheckboxGroup }, data, oldValue }: Cell) => {
    const rows = resolveRows();
    if (isCheckboxGroup) {
      const checkedRowAliasName = data?.value || oldValue;
      const checkedRow = rows.find(row => row.aliasName === checkedRowAliasName);
      updateGroup(
        rows
          .filter(row => row.checkboxGroupId === checkedRow?.checkboxGroupId)
          .map(row =>
            Object.assign(row, {
              value: (data?.value && row.aliasName === checkedRow?.aliasName) || false,
            }),
          ),
      );
    } else {
      updateRow(data);
    }
  };

  const getGroupRowParam = (param: CellEditorParams) => {
    const rows = resolveRows();
    const selectionListRows = rows.filter(row => row.checkboxGroupId === param.node.key);
    const checkedRow = rows.find(row => row.checkboxGroupId === param.node.key && !!row.value);
    const value = checkedRow ? checkedRow.aliasName : null;
    const label = checkedRow ? checkedRow.description : null;

    const newData = {
      group: true,
      dataType: DataTypes.CHECKBOX,
      value,
      selectionListId: value,
      selectionListItems: selectionListRows.map(item => ({
        value: item.aliasName,
        label: item.itemDisplayName || item.description,
      })),
    };

    return {
      ...param,
      data: newData,
      value,
      label,
    };
  };

  const checkboxCellClassRules = {
    ...defaultColumnDefinitionWithFilter.cellClassRules,
    'editable-cell': () => isInEditMode,
  };

  return {
    ...getDefaultGroupColumnDefinitionWithFilter('aliasName'),
    headerName: 'Value',
    field: 'value',
    minWidth: 200,
    flex: 1,
    editable: (param: Cell) => param.node.level === NodeLevel.ROOT && isInEditMode,
    cellRenderer: (param: CellEditorParams) => {
      if (param.node.group) {
        return filingTypeId === 'CONSOLIDATED'
          ? RendererFramework(getGroupRowParam(param))
          : StateOverrideRendererFramework(getGroupRowParam(param));
      }

      if (param.node.level === NodeLevel.CHILD) {
        return param.value ? 'Checked' : '';
      }

      return filingTypeId === 'CONSOLIDATED'
        ? RendererFramework(param)
        : StateOverrideRendererFramework(param);
    },
    cellEditorSelector: (param: CellEditorParams) =>
      param.node.group
        ? conditionalEditorSelector(getGroupRowParam(param))
        : conditionalEditorSelector(param),
    cellRendererParams: filingTypeId === 'CONSOLIDATED' ? { disabled: !isInEditMode } : null,
    cellClassRules:
      filingTypeId === 'CONSOLIDATED'
        ? checkboxCellClassRules
        : defaultColumnDefinitionWithFilter?.cellClassRules,
    onCellValueChanged,
  };
};
